import { type AxiosResponse } from "axios";

import { type UserAccessible } from "shared/resources";
import { type Nullable } from "shared/types";

import ApiClient from "./ApiClient";

export interface BaseInstantInsightsReportBulkRequest {
  instant_insights_report_ids: number[];
}

export interface InstantInsightsReportBulkShareRequest extends BaseInstantInsightsReportBulkRequest {
  shared_with: {
    access_level: string;
    user_id: number;
  }[];
}

export interface InstantInsightsReportBulkDuplicateRequest extends BaseInstantInsightsReportBulkRequest {
  organisation_team_id: Nullable<number>;
}

export interface InstantInsightsReportBulkSuccessResponse {
  success: boolean;
}

export interface InstantInsightsReportCreatePublicAccessKeyResponse {
  public_access_key: string;
}

export interface InstantInsightsReportRemovePublicAccessKeyResponse {
  success: boolean;
}

class InstantInsightsReportService extends ApiClient {
  constructor() {
    super("/instant_insights_reports");
  }

  archive(
    id: number
  ): Promise<AxiosResponse<InstantInsightsReportBulkSuccessResponse>> {
    return this.delete<InstantInsightsReportBulkSuccessResponse>(`/${id}`);
  }

  bulkArchive(
    reportIds: number[]
  ): Promise<AxiosResponse<InstantInsightsReportBulkSuccessResponse>> {
    const request: BaseInstantInsightsReportBulkRequest = {
      instant_insights_report_ids: reportIds,
    };

    return this.post<InstantInsightsReportBulkSuccessResponse>(
      "/bulk_archive",
      {
        data: request,
      }
    );
  }

  bulkDuplicate(
    reportIds: number[],
    organisationTeamId: Nullable<number> = null
  ): Promise<AxiosResponse<InstantInsightsReportBulkSuccessResponse>> {
    const request: InstantInsightsReportBulkDuplicateRequest = {
      instant_insights_report_ids: reportIds,
      organisation_team_id: organisationTeamId,
    };

    return this.post<InstantInsightsReportBulkSuccessResponse>(
      "/bulk_duplicate",
      {
        data: request,
      }
    );
  }

  bulkShare(
    reportIds: number[],
    sharedWith: UserAccessible[]
  ): Promise<AxiosResponse<InstantInsightsReportBulkSuccessResponse>> {
    const request: InstantInsightsReportBulkShareRequest = {
      instant_insights_report_ids: reportIds,
      shared_with: sharedWith.map((user) => ({
        access_level: user.accessLevel,
        user_id: user.userId,
      })),
    };

    return this.post<InstantInsightsReportBulkSuccessResponse>("/bulk_share", {
      data: request,
    });
  }

  createPublicAccessKey(
    id: number
  ): Promise<
    AxiosResponse<InstantInsightsReportCreatePublicAccessKeyResponse>
  > {
    return this.post<InstantInsightsReportCreatePublicAccessKeyResponse>(
      `/${id}/create_public_access_key`
    );
  }

  duplicate(
    id: number,
    organisationTeamId: Nullable<number> = null
  ): Promise<AxiosResponse<InstantInsightsReportBulkSuccessResponse>> {
    return this.post<InstantInsightsReportBulkSuccessResponse>(
      `/${id}/duplicate`,
      {
        data: { organisation_team_id: organisationTeamId },
      }
    );
  }

  removePublicAccessKey(
    id: number
  ): Promise<
    AxiosResponse<InstantInsightsReportRemovePublicAccessKeyResponse>
  > {
    return this.delete<InstantInsightsReportRemovePublicAccessKeyResponse>(
      `/${id}/remove_public_access_key`
    );
  }
}

export default new InstantInsightsReportService();
